import { useState, useEffect, useCallback } from 'react';
import { api, type ApiError } from '@/lib/api-client';
import { toast } from '@/utils/toast';
import { mapApiBeneficiary } from '@/features/beneficiaries/map-api-beneficiary';
import type {
  ClientDetail,
  Person,
  PersonRights,
  Beneficiary,
  ClientAccount,
  TransactionSummary,
  AuditLogEntry,
  ClientPricing,
  DocumentItem,
} from './types';

// ---------------------------------------------------------------------------
// API payload types
// ---------------------------------------------------------------------------

/**
 * Request body for POST /api/clients/:id/persons — mirrors CreatePersonDto
 * on the backend 1:1 (camelCase, identical field names). Empty strings for
 * optional fields must be coerced to null / undefined before submit.
 *
 * Permission booleans (canLogin + the four right flags) are wired through
 * to the webapp's CreateIndividualDto, which delegates to the shared
 * persistInitialPermissions util so a person added from the Persons tab
 * gets the same initial permissions UX as one added during onboarding.
 */
export interface CreatePersonPayload {
  roleId: number;
  firstName: string;
  lastName: string;
  email: string;
  gender?: string | null;
  dateOfBirth?: string | null;
  nationalityCountryCode?: string | null;
  residenceCountryCode?: string | null;
  telephone?: string | null;
  ownershipPercent?: number | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  addressCity?: string | null;
  addressState?: string | null;
  addressZip?: string | null;
  addressCountryCode?: string | null;
  isPep?: boolean;
  pepName?: string | null;
  pepRelationship?: string | null;
  usResident?: boolean;
  usTin?: string | null;
  residenceTin?: string | null;
  canLogin?: boolean;
  canViewTransactions?: boolean;
  canCreateTransfer?: boolean;
  /** #62 — approuver, et donc envoyer, un paiement préparé par un autre. */
  canApprovePayment?: boolean;
  canCreateFx?: boolean;
  canManageBeneficiary?: boolean;
  /**
   * Set when the form was filled from "Lookup existing" and the email was
   * kept: the person is associated with this company and their existing
   * portal login is reused instead of a second one being created.
   */
  associateExisting?: boolean;
}

export type UpdatePersonPayload = Partial<CreatePersonPayload>;

/** Identity fields an existing person brings to the Add person form. */
export interface PersonLookupFields {
  first_name: string;
  last_name: string;
  email: string;
  gender: string | null;
  date_of_birth: string | null;
  nationality_country_code: string | null;
  residence_country_code: string | null;
  telephone: string | null;
  address_line1: string | null;
  address_line2: string | null;
  address_city: string | null;
  address_state: string | null;
  address_zip: string | null;
  address_country_code: string | null;
  is_pep: boolean;
  pep_name: string | null;
  pep_relationship: string | null;
  us_resident: boolean;
  us_tin: string | null;
  residence_tin: string | null;
}

/** Response of POST /api/clients/:id/persons/lookup. */
export type PersonLookupResult =
  | { status: 'found'; person: PersonLookupFields }
  | { status: 'not_found' }
  | { status: 'already_associated' };

export interface UpdatePersonRightsPayload {
  can_view_transactions: boolean;
  can_create_transfer: boolean;
  /** #62 — approuver, et donc envoyer, un paiement préparé par un autre. */
  can_approve_payment: boolean;
  can_create_fx: boolean;
  can_manage_beneficiary: boolean;
}

/**
 * Request body for POST /api/clients/:id/beneficiaries — mirrors
 * CreateBeneficiaryDto on the backend 1:1 (camelCase, identical field
 * names). `companyName` is required when `beneficiaryTypeId === 2`
 * (business); `firstName`/`lastName` are required when
 * `beneficiaryTypeId === 1` (individual). Empty strings for optional
 * fields must be coerced to null / undefined before submit.
 */
export interface CreateBeneficiaryPayload {
  beneficiaryTypeId: number;
  label: string;
  companyName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  currencyCode: string;
  addressLine1?: string | null;
  addressLine2?: string | null;
  addressCity?: string | null;
  addressState?: string | null;
  addressZip?: string | null;
  addressCountryCode?: string | null;
  bankName?: string | null;
  bankCountryCode?: string | null;
  bankNetworkId?: number | null;
  bankCode?: string | null;
  accountNumber?: string | null;
  // Champs du Payment Formatting Guide (#70), tous facultatifs : le
  // corridor decide lesquels Convera reclame.
  taxId: string | null;
  email: string | null;
  phoneNumber: string | null;
  dateOfBirth: string | null;
  bankBranchCode: string | null;
  bankAccountType: string | null;
  bankBranchName: string | null;
  bankAddressLine1: string | null;
  bankAddressCity: string | null;
  bankAddressState: string | null;
  bankAddressZip: string | null;
}

export type UpdateBeneficiaryPayload = Partial<CreateBeneficiaryPayload>;

export interface UpdateKycPayload {
  kycStatusId: number;
}

/** Sets one rail's fees. The API takes one service per call (#66). */
export interface UpdatePricingPayload {
  payment_service_code: string;
  outgoing_fee_fixed_value: string;
  outgoing_fee_percent_value: string;
  incoming_fee_fixed_value: string;
  incoming_fee_percent_value: string;
}

/** The FX spread is global to the client and has its own endpoint. */
export interface UpdateFxSpreadPayload {
  fx_spread: string;
}

export interface UpdateClientPayload {
  // companyName is optional so a no-op save (operator opens the modal,
  // clicks Save without editing) becomes a true no-op rather than always
  // re-sending the unchanged company name. The EditClientModal validate()
  // guard still requires it to be non-empty when present.
  companyName?: string;
  legalFormId?: number | null;
  countryCode?: string | null;
  incorporationDate?: string | null;
  incorporationNumber?: string | null;
  contactEmail?: string | null;
  telephone?: string | null;
  registeredAddressLine1?: string | null;
  registeredAddressLine2?: string | null;
  registeredAddressCity?: string | null;
  registeredAddressState?: string | null;
  registeredAddressZip?: string | null;
  registeredAddressCountryCode?: string | null;
  businessAddressLine1?: string | null;
  businessAddressLine2?: string | null;
  businessAddressCity?: string | null;
  businessAddressState?: string | null;
  businessAddressZip?: string | null;
  businessAddressCountryCode?: string | null;
  businessSectorId?: number | null;
  tin?: string | null;
  website?: string | null;
  activityDescription?: string | null;
  purposeOfAccount?: string | null;
  intendedUse?: string | null;
  sourceOfIncome?: string | null;
  monthlyIncome?: number | null;
  estimatedIncomingTxCount?: number | null;
  estimatedIncomingVolume?: number | null;
  estimatedOutgoingTxCount?: number | null;
  estimatedOutgoingVolume?: number | null;
  // Separes par sens depuis #225.
  plannedCountriesIn?: string[] | null;
  plannedCountriesOut?: string[] | null;
  plannedCurrencies?: string[] | null;
}

export interface CreateAccountPayload {
  currency: string;
}

export type GrantPortalAccessStatus = 'granted' | 'already_exists';
export type ResendInvitationStatus = 'sent' | 'already_active' | 'no_portal_access';

// ---------------------------------------------------------------------------
// Hook return type
// ---------------------------------------------------------------------------

export interface UseClientDetailReturn {
  // Client
  client: ClientDetail | null;
  clientLoading: boolean;
  clientError: string | null;
  refreshClient: () => Promise<void>;

  // Client update
  updateClient: (payload: UpdateClientPayload) => Promise<void>;

  // KYC
  updateKyc: (payload: UpdateKycPayload) => Promise<void>;
  activateClient: (active: boolean) => Promise<void>;

  // Persons
  persons: Person[];
  personsLoading: boolean;
  loadPersons: () => Promise<void>;
  createPerson: (payload: CreatePersonPayload) => Promise<void>;
  lookupPerson: (email: string) => Promise<PersonLookupResult>;
  updatePerson: (personId: string, payload: UpdatePersonPayload) => Promise<void>;
  deletePerson: (personId: string) => Promise<void>;
  grantPortalAccess: (personId: string) => Promise<GrantPortalAccessStatus>;
  resendInvitation: (personId: string) => Promise<ResendInvitationStatus>;
  getPersonRights: (personId: string) => Promise<PersonRights>;
  updatePersonRights: (personId: string, payload: UpdatePersonRightsPayload) => Promise<void>;

  // Beneficiaries
  beneficiaries: Beneficiary[];
  beneficiariesLoading: boolean;
  loadBeneficiaries: () => Promise<void>;
  createBeneficiary: (payload: CreateBeneficiaryPayload) => Promise<void>;
  updateBeneficiary: (beneficiaryId: string, payload: UpdateBeneficiaryPayload) => Promise<void>;
  deleteBeneficiary: (beneficiaryId: string) => Promise<void>;

  // Accounts
  accounts: ClientAccount[];
  accountsLoading: boolean;
  loadAccounts: () => Promise<void>;
  createAccount: (payload: CreateAccountPayload) => Promise<void>;

  // Transactions
  transactions: TransactionSummary[];
  transactionsLoading: boolean;
  loadTransactions: () => Promise<void>;

  // Audit Log
  auditLog: AuditLogEntry[];
  auditLogLoading: boolean;
  loadAuditLog: () => Promise<void>;

  // Pricing
  pricing: ClientPricing | null;
  pricingLoading: boolean;
  loadPricing: () => Promise<void>;
  updatePricing: (
    services: UpdatePricingPayload[],
    fxSpread?: UpdateFxSpreadPayload,
  ) => Promise<void>;

  // Documents
  documents: DocumentItem[];
  documentsLoading: boolean;
  documentsError: string | null;
  loadDocuments: () => Promise<void>;
}

// ---------------------------------------------------------------------------
// Helper to extract error message
// ---------------------------------------------------------------------------

function getErrorMessage(err: unknown): string {
  if (err && typeof err === 'object' && 'message' in err) {
    const msg = (err as ApiError).message;
    return Array.isArray(msg) ? msg.join(', ') : msg;
  }
  return 'An unexpected error occurred';
}

// Beneficiary `(client_id, label)` is uniquely constrained at the DB level;
// the backend re-raises the PG error as 409 / a 500 carrying the raw "23505"
// or "duplicate" text. Either way the operator deserves a friendlier hint
// than the raw driver message.
function getBeneficiaryErrorMessage(err: unknown): string {
  if (err && typeof err === 'object') {
    const apiErr = err as Partial<ApiError>;
    const raw = Array.isArray(apiErr.message) ? apiErr.message.join(', ') : (apiErr.message ?? '');
    if (apiErr.statusCode === 409 || /23505|duplicate|unique/i.test(raw)) {
      return 'A beneficiary with this label already exists for this client. Please use a different label.';
    }
  }
  return getErrorMessage(err);
}

// ---------------------------------------------------------------------------
// API response mappers
// ---------------------------------------------------------------------------

function toIsoDate(value: unknown): string {
  if (typeof value !== 'string' || value.length === 0) return '';
  const tIndex = value.indexOf('T');
  return tIndex > 0 ? value.slice(0, tIndex) : value;
}

function mapApiPerson(raw: Record<string, unknown>, clientId: string): Person {
  const screeningFromResponse = raw.screeningStatusId;
  const approvalDate = raw.approvalScreeningDate;
  const screeningStatusId =
    typeof screeningFromResponse === 'number'
      ? screeningFromResponse
      : approvalDate != null
        ? 2
        : 1;
  const telephone = (raw.telephone as string | null) ?? null;
  return {
    id: String(raw.personId ?? ''),
    client_id: clientId,
    role_id: (raw.roleId as number | null) ?? null,
    first_name: (raw.firstName as string) ?? '',
    last_name: (raw.lastName as string) ?? '',
    email: (raw.email as string) ?? '',
    gender: (raw.gender as string | null) ?? null,
    date_of_birth: toIsoDate(raw.dateOfBirth),
    nationality_country_code: (raw.nationalityCountryCode as string | null) ?? null,
    residence_country_code: (raw.residenceCountryCode as string | null) ?? null,
    telephone,
    phone: telephone,
    ownership_percent: (raw.ownershipPercent as string | null) ?? null,
    address_line1: (raw.addressLine1 as string | null) ?? null,
    address_line2: (raw.addressLine2 as string | null) ?? null,
    address_city: (raw.addressCity as string | null) ?? null,
    address_state: (raw.addressState as string | null) ?? null,
    address_zip: (raw.addressZip as string | null) ?? null,
    address_country_code: (raw.addressCountryCode as string | null) ?? null,
    is_pep: (raw.isPep as boolean | null) ?? false,
    pep_name: (raw.pepName as string | null) ?? null,
    pep_relationship: (raw.pepRelationship as string | null) ?? null,
    us_resident: (raw.usResident as boolean | null) ?? false,
    us_tin: (raw.usTin as string | null) ?? null,
    residence_tin: (raw.residenceTin as string | null) ?? null,
    is_active: true,
    created_at: (raw.createdAt as string) ?? '',
    has_portal_access: Boolean(raw.hasPortalAccess ?? raw.has_portal_access),
    invitation_active: Boolean(raw.invitationActive ?? raw.invitation_active),
    screening_status_id: screeningStatusId,
  };
}

function asString(value: unknown): string {
  if (value === undefined || value === null) return '';
  return typeof value === 'string' ? value : String(value);
}

function pickString(source: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    const value = source[key];
    if (value !== undefined && value !== null) return asString(value);
  }
  return '';
}

export function mapWebappAccountToClientAccount(raw: unknown): ClientAccount {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const walletIdRaw = r.wallet_id ?? r.walletId ?? r.account_wallet_id ?? r.accountWalletId;
  const walletId =
    typeof walletIdRaw === 'number' || typeof walletIdRaw === 'string' ? walletIdRaw : undefined;
  return {
    id: pickString(r, 'id', 'client_account_id', 'clientAccountId'),
    client_id: pickString(r, 'client_id', 'clientId'),
    currency: pickString(r, 'currency', 'currency_code', 'currencyCode'),
    iban: pickString(r, 'iban', 'account_number', 'accountNumber'),
    available_balance: pickString(r, 'available_balance', 'availableBalance', 'available'),
    pending_in_balance: pickString(
      r,
      'pending_in_balance',
      'pendingInBalance',
      'pending_in',
      'pendingIn',
    ),
    pending_out_balance: pickString(
      r,
      'pending_out_balance',
      'pendingOutBalance',
      'pending_out',
      'pendingOut',
    ),
    reserved_balance: pickString(r, 'reserved_balance', 'reservedBalance', 'reserved'),
    created_at: pickString(r, 'created_at', 'createdAt'),
    wallet_id: walletId,
  };
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

const INVALID_CLIENT_ID = (id: string) => !id || id === 'undefined' || isNaN(Number(id));

export function useClientDetail(clientId: string): UseClientDetailReturn {
  const base = `/api/clients/${clientId}`;

  // --- Client ---
  const [client, setClient] = useState<ClientDetail | null>(null);
  const [clientLoading, setClientLoading] = useState(!INVALID_CLIENT_ID(clientId));
  const [clientError, setClientError] = useState<string | null>(
    INVALID_CLIENT_ID(clientId) ? 'Invalid client ID' : null,
  );

  const refreshClient = useCallback(async () => {
    if (INVALID_CLIENT_ID(clientId)) return;
    setClientLoading(true);
    setClientError(null);
    try {
      const data = await api.get<ClientDetail>(base);
      setClient(data);
    } catch (err) {
      const msg = getErrorMessage(err);
      setClientError(msg);
    } finally {
      setClientLoading(false);
    }
  }, [base, clientId]);

  useEffect(() => {
    void refreshClient();
  }, [refreshClient]);

  // --- Update Client ---
  const updateClient = useCallback(
    async (payload: UpdateClientPayload) => {
      try {
        const updated = await api.put<ClientDetail>(base, payload);
        setClient(updated);
        toast.success('Client updated');
      } catch (err) {
        const msg = getErrorMessage(err);
        toast.error(msg);
        throw err;
      }
    },
    [base],
  );

  // --- KYC ---
  const updateKyc = useCallback(
    async (payload: UpdateKycPayload) => {
      try {
        const updated = await api.put<ClientDetail>(`${base}/kyc`, payload);
        setClient(updated);
        toast.success('KYC status updated');
      } catch (err) {
        const msg = getErrorMessage(err);
        toast.error(msg);
        throw err;
      }
    },
    [base],
  );

  const activateClient = useCallback(
    async (active: boolean) => {
      try {
        const updated = await api.put<ClientDetail>(`${base}/activate`, { active });
        setClient(updated);
        toast.success(active ? 'Client activated' : 'Client deactivated');
      } catch (err) {
        const msg = getErrorMessage(err);
        toast.error(msg);
        throw err;
      }
    },
    [base],
  );

  // --- Persons ---
  const [persons, setPersons] = useState<Person[]>([]);
  const [personsLoading, setPersonsLoading] = useState(false);

  const loadPersons = useCallback(async () => {
    setPersonsLoading(true);
    try {
      const data = await api.get<Record<string, unknown>[]>(`${base}/persons`);
      setPersons(data.map((r) => mapApiPerson(r, clientId)));
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setPersonsLoading(false);
    }
  }, [base, clientId]);

  const createPerson = useCallback(
    async (payload: CreatePersonPayload) => {
      try {
        await api.post<Person>(`${base}/persons`, payload);
        toast.success('Person added');
        await loadPersons();
      } catch (err) {
        const msg = getErrorMessage(err);
        toast.error(msg);
        throw err;
      }
    },
    [base, loadPersons],
  );

  const lookupPerson = useCallback(
    async (email: string): Promise<PersonLookupResult> => {
      try {
        return await api.post<PersonLookupResult>(`${base}/persons/lookup`, { email });
      } catch (err) {
        toast.error(getErrorMessage(err));
        throw err;
      }
    },
    [base],
  );

  const updatePerson = useCallback(
    async (personId: string, payload: UpdatePersonPayload) => {
      try {
        await api.put<Person>(`${base}/persons/${personId}`, payload);
        toast.success('Person updated');
        await loadPersons();
      } catch (err) {
        const msg = getErrorMessage(err);
        toast.error(msg);
        throw err;
      }
    },
    [base, loadPersons],
  );

  const deletePerson = useCallback(
    async (personId: string) => {
      try {
        await api.delete<void>(`${base}/persons/${personId}`);
        toast.success('Person deleted');
        await loadPersons();
      } catch (err) {
        toast.error(getErrorMessage(err));
        throw err;
      }
    },
    [base, loadPersons],
  );

  const grantPortalAccess = useCallback(
    async (personId: string): Promise<GrantPortalAccessStatus> => {
      try {
        const data = await api.post<{ status: GrantPortalAccessStatus }>(
          `${base}/persons/${personId}/grant-portal-access`,
          {},
        );
        await loadPersons();
        if (data.status === 'granted') {
          toast.success('Portal access granted. Invitation email sent.');
        } else {
          toast.info('This person already has portal access.');
        }
        return data.status;
      } catch (err) {
        toast.error(getErrorMessage(err));
        throw err;
      }
    },
    [base, loadPersons],
  );

  const resendInvitation = useCallback(
    async (personId: string): Promise<ResendInvitationStatus> => {
      try {
        const data = await api.post<{ status: ResendInvitationStatus }>(
          `${base}/persons/${personId}/resend-invitation`,
          {},
        );
        if (data.status === 'sent') {
          toast.success('Invitation email sent.');
        } else if (data.status === 'already_active') {
          toast.info(
            'This person has already activated their account. Use the Forgot Password flow if they need a new password.',
          );
        } else {
          toast.error('No portal access yet — grant access first.');
        }
        return data.status;
      } catch (err) {
        toast.error(getErrorMessage(err));
        throw err;
      }
    },
    [base],
  );

  const getPersonRights = useCallback(
    async (personId: string): Promise<PersonRights> => {
      try {
        // CBS API wraps the rights response as { permissions: string[] }
        // (B20). Unwrap before constructing the Set, otherwise
        // `new Set({permissions: [...]})` throws "object is not iterable".
        // The `?? []` is defensive against a malformed payload during
        // partial-deploy windows.
        const response = await api.get<{ permissions: string[] }>(
          `${base}/persons/${personId}/rights`,
        );
        const set = new Set(response.permissions ?? []);
        return {
          person_id: personId,
          can_view_transactions: set.has('ACCOUNT_VIEW'),
          can_create_transfer: set.has('TRANSFER'),
          can_approve_payment: set.has('APPROVE_PAYMENT'),
          can_create_fx: set.has('FX'),
          can_manage_beneficiary: set.has('BENEFICIARY'),
          created_at: new Date().toISOString(),
        };
      } catch (err) {
        // 404 = person has no portal access yet (B21). Don't toast — return
        // an empty rights object so the modal renders with all toggles off.
        // PersonsTab already hides the Permissions button on
        // has_portal_access=false; this branch covers stale-state races.
        if (
          err &&
          typeof err === 'object' &&
          'statusCode' in err &&
          (err as ApiError).statusCode === 404
        ) {
          return {
            person_id: personId,
            can_view_transactions: false,
            can_create_transfer: false,
            can_approve_payment: false,
            can_create_fx: false,
            can_manage_beneficiary: false,
            created_at: new Date().toISOString(),
          };
        }
        toast.error(getErrorMessage(err));
        throw err;
      }
    },
    [base],
  );

  const updatePersonRights = useCallback(
    async (personId: string, payload: UpdatePersonRightsPayload) => {
      try {
        // The backend expects { permissions: ['TRANSFER', 'FX', ...] } —
        // a flat list of portal-permission codes. We translate the booleans
        // the UI maintains here so the local form stays human-readable while
        // the wire payload matches the DTO.
        //
        // Every code the person should keep must be listed: the endpoint
        // replaces the whole set rather than merging. Until this screen knew
        // about APPROVE_PAYMENT, saving it silently revoked the right (#62,
        // SCP-015 §7).
        const permissions: string[] = [];
        if (payload.can_view_transactions) permissions.push('ACCOUNT_VIEW');
        if (payload.can_create_transfer) permissions.push('TRANSFER');
        if (payload.can_approve_payment) permissions.push('APPROVE_PAYMENT');
        if (payload.can_create_fx) permissions.push('FX');
        if (payload.can_manage_beneficiary) permissions.push('BENEFICIARY');
        await api.put<PersonRights>(`${base}/persons/${personId}/rights`, {
          permissions,
        });
        toast.success('Permissions updated');
      } catch (err) {
        toast.error(getErrorMessage(err));
        throw err;
      }
    },
    [base],
  );

  // --- Beneficiaries ---
  const [beneficiaries, setBeneficiaries] = useState<Beneficiary[]>([]);
  const [beneficiariesLoading, setBeneficiariesLoading] = useState(false);

  const loadBeneficiaries = useCallback(async () => {
    setBeneficiariesLoading(true);
    try {
      const data = await api.get<Record<string, unknown>[]>(`${base}/beneficiaries`);
      setBeneficiaries(data.map((r) => mapApiBeneficiary(r, clientId)));
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBeneficiariesLoading(false);
    }
  }, [base, clientId]);

  const createBeneficiary = useCallback(
    async (payload: CreateBeneficiaryPayload) => {
      try {
        await api.post<Beneficiary>(`${base}/beneficiaries`, payload);
        toast.success('Beneficiary added');
        await loadBeneficiaries();
      } catch (err) {
        toast.error(getBeneficiaryErrorMessage(err));
        throw err;
      }
    },
    [base, loadBeneficiaries],
  );

  const updateBeneficiary = useCallback(
    async (beneficiaryId: string, payload: UpdateBeneficiaryPayload) => {
      try {
        await api.put<Beneficiary>(`${base}/beneficiaries/${beneficiaryId}`, payload);
        toast.success('Beneficiary updated');
        await loadBeneficiaries();
      } catch (err) {
        toast.error(getBeneficiaryErrorMessage(err));
        throw err;
      }
    },
    [base, loadBeneficiaries],
  );

  const deleteBeneficiary = useCallback(
    async (beneficiaryId: string) => {
      try {
        await api.delete<void>(`${base}/beneficiaries/${beneficiaryId}`);
        toast.success('Beneficiary deactivated');
        await loadBeneficiaries();
      } catch (err) {
        toast.error(getErrorMessage(err));
        throw err;
      }
    },
    [base, loadBeneficiaries],
  );

  // --- Accounts ---
  const [accounts, setAccounts] = useState<ClientAccount[]>([]);
  const [accountsLoading, setAccountsLoading] = useState(false);

  const loadAccounts = useCallback(async () => {
    setAccountsLoading(true);
    try {
      const raw = await api.get<unknown[]>(`${base}/accounts`);
      const items = Array.isArray(raw) ? raw : [];
      const mapped = items.map((entry) => mapWebappAccountToClientAccount(entry));
      setAccounts(mapped);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setAccountsLoading(false);
    }
  }, [base]);

  const createAccount = useCallback(
    async (payload: CreateAccountPayload) => {
      try {
        await api.post<ClientAccount>(`${base}/accounts`, payload);
        toast.success('Account created');
        await loadAccounts();
      } catch (err) {
        toast.error(getErrorMessage(err));
        throw err;
      }
    },
    [base, loadAccounts],
  );

  // --- Transactions ---
  const [transactions, setTransactions] = useState<TransactionSummary[]>([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);

  const loadTransactions = useCallback(async () => {
    setTransactionsLoading(true);
    try {
      const data = await api.get<TransactionSummary[]>(`${base}/transactions`);
      setTransactions(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setTransactionsLoading(false);
    }
  }, [base]);

  // --- Audit Log ---
  const [auditLog, setAuditLog] = useState<AuditLogEntry[]>([]);
  const [auditLogLoading, setAuditLogLoading] = useState(false);

  const loadAuditLog = useCallback(async () => {
    setAuditLogLoading(true);
    try {
      const data = await api.get<AuditLogEntry[]>(`${base}/audit-log`);
      setAuditLog(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setAuditLogLoading(false);
    }
  }, [base]);

  // --- Pricing ---
  const [pricing, setPricing] = useState<ClientPricing | null>(null);
  const [pricingLoading, setPricingLoading] = useState(false);

  const loadPricing = useCallback(async () => {
    setPricingLoading(true);
    try {
      const data = await api.get<ClientPricing>(`${base}/pricing`);
      setPricing(data);
    } catch (err) {
      // 404 = no pricing config, use defaults (handled in component)
      if (
        err &&
        typeof err === 'object' &&
        'statusCode' in err &&
        (err as ApiError).statusCode === 404
      ) {
        setPricing(null);
      } else {
        toast.error(getErrorMessage(err));
      }
    } finally {
      setPricingLoading(false);
    }
  }, [base]);

  /**
   * Saves the rails the operator changed, then the spread if it moved (#66).
   *
   * The API takes one rail per call, so a screen-level save fans out. They run
   * sequentially rather than in parallel: on a partial failure the operator
   * sees which rail broke, and the rails already saved stay saved.
   */
  const updatePricing = useCallback(
    async (services: UpdatePricingPayload[], fxSpread?: UpdateFxSpreadPayload) => {
      try {
        for (const payload of services) {
          await api.put(`${base}/pricing`, payload);
        }
        if (fxSpread) {
          // Le spread n'a pas de route sous `/clients/:id` : il se pose par
          // `POST /fx/margin`, qui verifie le client, arrondit a six decimales
          // et ecrit la piste d'audit. L'appel a `PUT /clients/:id/fx-spread`
          // n'a jamais eu de route en face et rendait « Cannot PUT » (#244).
          //
          // `toFixed` plutot que la saisie brute : le formulaire valide avec
          // `parseFloat`, qui accepte « .5 », alors que l'API exige
          // `^\d+(\.\d+)?$`.
          await api.post('/api/fx/margin', {
            client_id: Number(clientId),
            fx_spread: Number(fxSpread.fx_spread).toFixed(6),
          });
        }
        const refreshed = await api.get<ClientPricing>(`${base}/pricing`);
        setPricing(refreshed);
        toast.success('Pricing updated');
      } catch (err) {
        toast.error(getErrorMessage(err));
        throw err;
      }
    },
    [base],
  );

  // --- Documents ---
  const [documents, setDocuments] = useState<DocumentItem[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [documentsError, setDocumentsError] = useState<string | null>(null);

  const loadDocuments = useCallback(async () => {
    setDocumentsLoading(true);
    setDocumentsError(null);
    try {
      const data = await api.get<DocumentItem[]>(`${base}/documents`);
      setDocuments(data);
    } catch (err) {
      const msg = getErrorMessage(err);
      setDocumentsError(msg);
    } finally {
      setDocumentsLoading(false);
    }
  }, [base]);

  return {
    client,
    clientLoading,
    clientError,
    refreshClient,
    updateClient,
    updateKyc,
    activateClient,
    persons,
    personsLoading,
    loadPersons,
    createPerson,
    lookupPerson,
    updatePerson,
    deletePerson,
    grantPortalAccess,
    resendInvitation,
    getPersonRights,
    updatePersonRights,
    beneficiaries,
    beneficiariesLoading,
    loadBeneficiaries,
    createBeneficiary,
    updateBeneficiary,
    deleteBeneficiary,
    accounts,
    accountsLoading,
    loadAccounts,
    createAccount,
    transactions,
    transactionsLoading,
    loadTransactions,
    auditLog,
    auditLogLoading,
    loadAuditLog,
    pricing,
    pricingLoading,
    loadPricing,
    updatePricing,
    documents,
    documentsLoading,
    documentsError,
    loadDocuments,
  };
}
